<template>
  <div class="basket-kit-description">
    <p class="basket-kit-description__article">
      {{ result.messages?.art + result.product.article }}
    </p>
    <div class="basket-kit-description__row">
      <a
        v-if="result.product.type"
        :href="result.product.detailPage"
        class="basket-kit-description__title"
        >{{ result.product.type }}</a
      >
      <DefLabels :result="result.product.labels" />
    </div>
    <a :href="result.product.detailPage" class="basket-kit-description__text">{{
      result.product.name
    }}</a>
    <DefProductStatus :result="result.product">
      <template #isAvailable>
        <p class="available">{{ result.messages.inStock }}</p>
      </template>
      <p class="notAvailable">{{ result.messages.outOfStock }}</p>
    </DefProductStatus>
    <BasketKitSKU
      :messages="result.messages"
      :sizes="result?.product?.skuMatrix?.SIZES"
      @changeSize="$emit('changeSize', $event)"
    />

    <div v-if="isAvailable" class="basket-kit-description__prices">
      <div v-if="result?.product.price">
        <DefPrice v-if="result?.product.price.printPrice">{{
          result?.product.price.printPrice
        }}</DefPrice>
        <DefPriceOld v-if="result?.product.price.printPriceOld">{{
          result?.product.price.printPriceOld
        }}</DefPriceOld>
      </div>
    </div>
    <DefBuy v-if="isAvailable" :result="result?.product" @click="handlerBuy">
      <template #inBasket>
        <span>{{ result?.messages.inBasket }}</span>
      </template>
      {{ result?.messages.addToBasket }}
    </DefBuy>
  </div>
</template>

<script lang="ts" setup>
import { computed, defineProps } from 'vue';
import { IProduct } from '@/interfaces/IProduct/IProduct';
import { IMessages } from '@/interfaces/IMessages';
import DefLabels from '@components/Def/DefLabels/DefLabels.vue';
import DefProductStatus from '@components/Def/DefProductStatus/DefProductStatus.vue';
import BasketKitSKU from '@components/Basket/BasketKits/BasketKitSKU.vue';
import DefPrice from '@components/Def/DefPrice/DefPrice.vue';
import DefBuy from '@components/Def/DefBuy/DefBuy.vue';
import DefPriceOld from '@components/Def/DefPriceOld/DefPriceOld.vue';
import useModalAside from '@use/useModalAside/useModalAside';

import { store } from '@store/index';

const props = defineProps<{ result: { product: IProduct; messages: IMessages } }>();

const isAvailable = computed(() => {
  if (props.result.product?.offer) return props.result.product?.offer?.isAvailable;
  return props.result.product?.isAvailable;
});

const modalAside = useModalAside();

const handlerBuy = async () => {
  await modalAside.hide();
};
</script>

<style lang="scss" scoped>
.basket-kit-description {
  &__article {
    font-size: rem(11);
    line-height: 110%;
    color: var(--color-dark-grey);
    margin-bottom: 8px;
  }
  &__row {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }
  &__title {
    font-weight: 600;
    font-size: rem(24);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    margin-bottom: 0;
    margin-right: 12px;
    line-height: 100%;
  }
  &__text {
    display: block;
    font-weight: 500;
    font-size: rem(14);
    line-height: 125%;
    color: var(--color-dark-grey);
    margin-bottom: 16px;
  }
  &__prices {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }
  :deep(.def-price) {
    font-size: rem(20);
    font-weight: bold;
  }
  :deep(.def-price-old) {
    font-size: rem(14);
    margin-left: 12px;
  }
  :deep(.basket-kit-sku) {
    margin-bottom: 24px;
  }
  :deep(.def-buy) {
    width: 100%;
    margin-bottom: 8px;
  }
  :deep(.def-product-status) {
    margin-bottom: 20px;
  }
}
</style>
